import { useId, useMemo, useState } from 'react';
import { RESET_PATH, SIGN_IN_PATH } from '@zevik/schemas';
import { requestReset, resetPassword } from '../../credential/api';
import { LINK_PROBLEMS, tokenFromLink } from '../../credential/link';
import { passwordProblem } from '../../credential/password-rule';
import { CredentialShell, Refused } from './CredentialShell';
import { PasswordField } from './PasswordField';

/**
 * `/reset`, in its two states (docs/DECISIONS.md ZD-118 §1, §2).
 *
 * With no token it is the request form: an email, and the API's own 202
 * sentence back whatever the email was — the page adds nothing that might say
 * whether it is an account. With a token it is the set-password form. A reset
 * does **not** sign the person in (the API clears the cookie and ends every
 * other session), so success sends them to sign in with the password they
 * just chose, and says why.
 */
export function ResetPage(): React.JSX.Element {
  const link = useMemo(() => tokenFromLink(window.location.search), []);
  if (link.kind === 'absent') return <RequestForm />;
  if (link.kind === 'malformed') {
    return (
      <CredentialShell title="Choose a new password">
        <p className="cred__reason">{LINK_PROBLEMS.malformed}</p>
        <p className="cred__foot">
          <a href={RESET_PATH}>Request a new link</a> if you cannot open the one you were sent.
        </p>
      </CredentialShell>
    );
  }
  return <SetForm token={link.token} />;
}

function RequestForm(): React.JSX.Element {
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [accepted, setAccepted] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setRefusal(null);
    setSending(true);
    try {
      const outcome = await requestReset(email.trim());
      if (!outcome.ok) {
        setRefusal(outcome.message);
        return;
      }
      setAccepted(outcome.value);
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  if (accepted !== null) {
    return (
      <CredentialShell title="Reset your password">
        <p className="cred__lead">{accepted}</p>
        <p className="cred__foot">
          The link works once and expires in an hour. If nothing arrives, the address may not have a
          password yet — access is given by invitation, from the person who administers Zevik for
          your organisation.
        </p>
      </CredentialShell>
    );
  }

  return (
    <CredentialShell title="Reset your password">
      <p className="cred__lead">Enter the email you sign in with and a link will be sent to it.</p>
      <form
        className="cred__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="cred__field">
          <label className="field" htmlFor={emailId}>
            Email
          </label>
          <input
            id={emailId}
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            required
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>
        {refusal === null ? null : <Refused message={refusal} />}
        <div className="cred__actions">
          <button type="submit" className="submit" disabled={sending}>
            {sending ? 'Sending…' : 'Send a reset link'}
          </button>
          <a className="cred__aside" href={SIGN_IN_PATH}>
            Back to sign in
          </a>
        </div>
      </form>
    </CredentialShell>
  );
}

function SetForm({ token }: { token: string }): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (): Promise<void> => {
    setRefusal(null);
    const rule = passwordProblem(password);
    setProblem(rule);
    if (rule !== null) return;
    setSending(true);
    try {
      const outcome = await resetPassword(token, password);
      if (!outcome.ok) {
        setRefusal(outcome.message);
        return;
      }
      setPassword('');
      setDone(true);
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <CredentialShell title="Your password is set">
        <p className="cred__lead">
          Every device that was signed in to this account has been signed out, including this one.
          Sign in with the password you just chose.
        </p>
        <div className="cred__actions">
          <a className="submit submit--link" href={SIGN_IN_PATH}>
            Sign in
          </a>
        </div>
      </CredentialShell>
    );
  }

  return (
    <CredentialShell title="Choose a new password">
      <p className="cred__lead">
        This link works once. Setting the password signs out every device currently signed in to
        this account.
      </p>
      <form
        className="cred__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <PasswordField value={password} onChange={setPassword} label="New password" />
        {problem === null ? null : <Refused message={problem} />}
        {refusal === null ? null : (
          <>
            <Refused message={refusal} />
            <p className="cred__foot">
              A reset link works once and for an hour. <a href={RESET_PATH}>Request a new one</a>.
            </p>
          </>
        )}
        <div className="cred__actions">
          <button type="submit" className="submit" disabled={sending}>
            {sending ? 'Setting…' : 'Set password'}
          </button>
        </div>
      </form>
    </CredentialShell>
  );
}
